/**
 * Telemetry bootstrap — per-integration activation.
 *
 * Each SaaS integration wakes up independently the moment its ID env var is
 * set — no master switch. This lets preview validate GA4/HubSpot/Tawk.to/
 * Google Forms once the operator pastes the IDs, while Sentry stays gated
 * behind `REACT_APP_ENABLE_SENTRY=true` so the free error budget is reserved
 * for production.
 *
 * Localhost (hostname === 'localhost' or '127.0.0.1') is additionally
 * suppressed for Sentry and HubSpot to avoid dev noise.
 */
import * as Sentry from '@sentry/react';
import { hasConsent } from './consent';

const isLocalhost = typeof window !== 'undefined'
  && ['localhost', '127.0.0.1', '0.0.0.0'].includes(window.location.hostname);

const cfg = {
  env: process.env.REACT_APP_ENV || 'preview',
  // Master switches (default OFF unless explicitly === 'true')
  sentryEnabled: process.env.REACT_APP_ENABLE_SENTRY === 'true',
  analyticsEnabled: process.env.REACT_APP_ENABLE_ANALYTICS === 'true',
  hubspotEnabled: process.env.REACT_APP_ENABLE_HUBSPOT === 'true',
  tawktoEnabled: process.env.REACT_APP_ENABLE_TAWKTO === 'true',
  // Per-integration IDs
  sentryDsn: process.env.REACT_APP_SENTRY_DSN || '',
  gaId: process.env.REACT_APP_GA_MEASUREMENT_ID || '',
  hubspotPortalId: process.env.REACT_APP_HUBSPOT_PORTAL_ID || '',
  tawktoPropertyId: process.env.REACT_APP_TAWKTO_PROPERTY_ID || '',
  tawktoWidgetId: process.env.REACT_APP_TAWKTO_WIDGET_ID || 'default',
  feedbackFormUrl: process.env.REACT_APP_FEEDBACK_FORM_URL || '',
};

// Per-integration "live" booleans — requires BOTH the master switch AND the ID.
// Sentry+HubSpot additionally skip localhost to avoid dev noise.
export const INTEGRATIONS = {
  sentry: Boolean(cfg.sentryEnabled && cfg.sentryDsn && !isLocalhost),
  ga4: Boolean(cfg.analyticsEnabled && cfg.gaId),
  hubspot: Boolean(cfg.hubspotEnabled && cfg.hubspotPortalId && !isLocalhost),
  tawkto: Boolean(cfg.tawktoEnabled && cfg.tawktoPropertyId),
  googleForms: Boolean(cfg.feedbackFormUrl),
};

let initialized = false;

function injectScript({ src, async = true, id, onload }) {
  if (id && document.getElementById(id)) return;
  const s = document.createElement('script');
  s.src = src;
  s.async = async;
  if (id) s.id = id;
  if (onload) s.onload = onload;
  document.head.appendChild(s);
}

function initSentry() {
  if (!INTEGRATIONS.sentry) return;
  try {
    Sentry.init({
      dsn: cfg.sentryDsn,
      environment: cfg.env,
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: 0.1,
    });
  } catch (_e) { /* telemetry must never break the app */ }
}

function initGA() {
  if (!INTEGRATIONS.ga4) return;
  injectScript({
    src: `https://www.googletagmanager.com/gtag/js?id=${cfg.gaId}`,
    id: 'ga4-loader',
  });
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', cfg.gaId, { send_page_view: true });
}

function initHubSpot() {
  if (!INTEGRATIONS.hubspot) return;
  injectScript({
    src: `//js.hs-scripts.com/${cfg.hubspotPortalId}.js`,
    id: 'hs-script-loader',
  });
}

function initTawkto() {
  if (!INTEGRATIONS.tawkto) return;
  window.Tawk_API = window.Tawk_API || {};
  window.Tawk_LoadStart = new Date();
  window.Tawk_API.onLoad = function () {
    try {
      window.Tawk_API.setAttributes({
        'welcome-message': 'Hello! How can we help you with CBAM compliance?',
      }, () => {});
    } catch (_e) { /* ignore */ }
  };
  injectScript({
    src: `https://embed.tawk.to/${cfg.tawktoPropertyId}/${cfg.tawktoWidgetId}`,
    id: 'tawkto-loader',
  });
}

export function initTelemetry() {
  if (initialized) return;
  // HARD GDPR gate: do not inject any third-party script unless the user has
  // explicitly accepted. The gate is re-evaluated after a consent change via
  // a window reload triggered by CookieConsent.decide().
  if (!hasConsent()) return;
  initialized = true;
  initSentry();
  initGA();
  initHubSpot();
  initTawkto();
}

/**
 * Track a business event across every live integration.
 * Silently skipped when the user has not accepted cookies.
 */
export function track(eventName, props = {}) {
  if (!hasConsent()) return;
  try {
    if (INTEGRATIONS.ga4 && typeof window.gtag === 'function') {
      window.gtag('event', eventName, props);
    }
    if (INTEGRATIONS.hubspot && Array.isArray(window._hsq)) {
      window._hsq.push(['trackCustomBehavioralEvent', { name: eventName, properties: props }]);
    }
    if (INTEGRATIONS.sentry) {
      Sentry.addBreadcrumb({ category: 'analytics', message: eventName, data: props, level: 'info' });
    }
  } catch (_e) { /* ignore */ }
}

/**
 * Identify the signed-in user across every live integration.
 * Silently skipped when the user has not accepted cookies.
 */
export function identify(user) {
  if (!user || !hasConsent()) return;
  try {
    if (INTEGRATIONS.ga4 && typeof window.gtag === 'function') {
      window.gtag('set', { user_id: user._id || user.id });
    }
    if (INTEGRATIONS.hubspot && Array.isArray(window._hsq) && user.email) {
      window._hsq.push(['identify', { email: user.email, id: user._id || user.id }]);
    }
    if (INTEGRATIONS.sentry) {
      Sentry.setUser({ id: user._id || user.id, email: user.email });
    }
  } catch (_e) { /* ignore */ }
}

export const telemetryConfig = cfg;

// Canonical event names — single source of truth.
export const EVENTS = {
  SIGN_IN: 'login',
  SIGN_UP: 'signup',
  CSV_UPLOADED: 'upload_csv',
  PCF_CALCULATED: 'calculate_pcf',
  CHECKOUT_STARTED: 'subscribe_start',
  SUBSCRIPTION_ACTIVATED: 'subscribe_success',
  SHIPMENT_CREATED: 'shipment_created',
  LEAD_SUBMITTED: 'book_demo',
};
